import type { OrgFeatureFlagKey } from '../../hooks/useOrgFeatureFlags';
import type { PlanLimits } from '../../types/billing';
import React from 'react';
import {
  SpaceDashboardOutlined,
  Inventory2Outlined,
  KeyOutlined,
  ScienceOutlined,
  HubOutlined,
  ShowChartOutlined,
  LocalOfferOutlined,
  ReceiptLongOutlined,
  StorefrontOutlined,
  GroupOutlined,
  TuneOutlined,
  SavingsOutlined,
  QrCode2Outlined,
  CategoryOutlined,
  SupervisedUserCircleOutlined,
  StoreMallDirectoryOutlined,
  BusinessCenterOutlined,
  BusinessOutlined,
  CloudDownloadOutlined,
  HistoryOutlined,
  WorkHistoryOutlined,
  RequestQuoteOutlined,
  SellOutlined,
  SettingsOutlined,
  CampaignOutlined,
  FactCheckOutlined,
  FindReplaceOutlined,
  AutoFixHighOutlined,
  WidgetsOutlined,
  FlagOutlined,
  PriceChangeOutlined,
  PriceCheckOutlined,
  ImageOutlined,
  HowToRegOutlined,
  ScheduleOutlined,
  AccountCircleOutlined,
} from '@mui/icons-material';

/** Live counts the shell resolves at render time and hangs off a nav row. */
export type NavBadgeKey = 'approvals' | 'syncSelection';

export interface NavLeaf {
  /** Stable id — used for pins, palette keys and React keys. Never localised. */
  id: string;
  /** i18n key under `shell.item.*`. */
  labelKey: string;
  icon: React.ReactNode;
  path: string;
  /** Extra route patterns that should light this row up. */
  matchPaths?: string[];
  /** Admin menu switch (`ui_*` flag) that is on unless turned off; see `isOnUnlessOff`. */
  uiSwitch?: OrgFeatureFlagKey;
  /** Server-side pre-release flag for the caller's organization (`/feature-flags/me`). */
  orgFlag?: OrgFeatureFlagKey;
  /** Requires an explicit store Product QA grant (`userProfile.productQaEnabled`). */
  requiresProductQa?: boolean;
  /**
   * Only for someone who manages a store group — a buying group or a head
   * office. `GET /session/organizations` says who that is (`groups`).
   */
  requiresGroupManager?: boolean;
  badgeKey?: NavBadgeKey;
  /**
   * The boolean plan feature the page is sold under. While a store's trial
   * gives it a feature its plan lacks, the row carries the tier that sells it
   * (MOT-729); it never hides the row. With `show_locked_plan_features` on,
   * a plan that lacks it greys the row instead of letting it look available.
   */
  planFeature?: keyof PlanLimits;
}

export interface NavGroup {
  id: string;
  /** i18n key under `shell.group.*`. */
  labelKey: string;
  icon: React.ReactNode;
  items: NavLeaf[];
}

export interface NavTree {
  /** Ungrouped rows, rendered above the groups. */
  roots: NavLeaf[];
  groups: NavGroup[];
}

export type ShellRole = 'admin' | 'store' | 'vendor';

/**
 * Rows every signed-in role gets, in the order the handoff shows them: the
 * dashboard first, then the catalog entry point.
 */
const rootItems: NavLeaf[] = [
  {
    id: 'dashboard',
    labelKey: 'dashboard',
    icon: <SpaceDashboardOutlined />,
    path: '/app/dashboard',
  },
  {
    id: 'product-browser',
    labelKey: 'productBrowser',
    icon: <Inventory2Outlined />,
    path: '/app/products',
    matchPaths: ['/app/products', '/app/cameras', '/app/compare'],
    uiSwitch: 'ui_product_browser',
    badgeKey: 'syncSelection',
  },
];

const developerGroup: NavGroup = {
  id: 'developer',
  labelKey: 'developer',
  icon: <KeyOutlined />,
  items: [
    {
      id: 'api-keys',
      labelKey: 'apiKeys',
      icon: <KeyOutlined />,
      path: '/app/api-keys',
      uiSwitch: 'ui_api_access',
    },
    {
      id: 'api-testing',
      labelKey: 'apiTesting',
      icon: <ScienceOutlined />,
      path: '/app/api-test',
      uiSwitch: 'ui_api_access',
    },
  ],
};

const adminTree: NavTree = {
  roots: rootItems,
  groups: [
    {
      id: 'brands-vendors',
      labelKey: 'brandsVendors',
      icon: <BusinessCenterOutlined />,
      items: [
        {
          id: 'brands-directory',
          labelKey: 'directory',
          icon: <BusinessCenterOutlined />,
          path: '/app/admin/brands-vendors',
        },
        {
          id: 'brands-logos',
          labelKey: 'logoLibrary',
          icon: <ImageOutlined />,
          path: '/app/admin/brands-vendors/logos',
        },
        {
          id: 'brands-organizations',
          labelKey: 'organizations',
          icon: <BusinessOutlined />,
          path: '/app/admin/brands-vendors/organizations',
        },
        {
          id: 'brands-approvals',
          labelKey: 'approvals',
          icon: <HowToRegOutlined />,
          path: '/app/admin/brands-vendors/approvals',
          badgeKey: 'approvals',
        },
        {
          id: 'brands-dealer-lists',
          labelKey: 'dealerLists',
          icon: <FactCheckOutlined />,
          path: '/app/admin/brands-vendors/dealer-lists',
        },
        {
          id: 'brands-dealer-authorizations',
          labelKey: 'dealerAuthorizations',
          icon: <HowToRegOutlined />,
          path: '/app/admin/brands-vendors/dealer-authorizations',
        },
        {
          id: 'brands-price-clocks',
          labelKey: 'priceClocks',
          icon: <ScheduleOutlined />,
          path: '/app/admin/brands-vendors/price-clocks',
        },
      ],
    },
    {
      id: 'retail-partners',
      labelKey: 'retailPartners',
      icon: <StoreMallDirectoryOutlined />,
      items: [
        {
          id: 'admin-stores',
          labelKey: 'stores',
          icon: <StoreMallDirectoryOutlined />,
          path: '/app/admin/stores',
        },
        {
          id: 'admin-store-sync',
          labelKey: 'storeErpSync',
          icon: <HubOutlined />,
          path: '/app/admin/store-sync-health',
        },
        {
          id: 'admin-organization-groups',
          labelKey: 'organizationGroups',
          icon: <GroupOutlined />,
          path: '/app/admin/organization-groups',
        },
      ],
    },
    {
      id: 'pricing-promotions',
      labelKey: 'pricingPromotions',
      icon: <LocalOfferOutlined />,
      items: [
        {
          id: 'admin-pricing-ingestion',
          labelKey: 'pricingIngestion',
          icon: <CloudDownloadOutlined />,
          path: '/app/admin/pricing-ingestion',
        },
        {
          id: 'admin-map-pricing',
          labelKey: 'mapPricing',
          icon: <LocalOfferOutlined />,
          path: '/app/admin/map-pricing',
        },
        {
          id: 'admin-rebates',
          labelKey: 'rebatePrograms',
          icon: <SavingsOutlined />,
          path: '/app/admin/rebates',
        },
      ],
    },
    {
      id: 'data-operations',
      labelKey: 'dataOperations',
      icon: <CloudDownloadOutlined />,
      items: [
        {
          id: 'admin-products',
          labelKey: 'products',
          icon: <CategoryOutlined />,
          path: '/app/admin/products',
        },
        {
          id: 'admin-staged-products',
          labelKey: 'stagedProducts',
          icon: <FactCheckOutlined />,
          path: '/app/admin/staged-products',
        },
        {
          id: 'admin-product-submissions',
          labelKey: 'productSubmissions',
          icon: <FactCheckOutlined />,
          path: '/app/admin/product-submissions',
        },
        {
          id: 'admin-product-issues',
          labelKey: 'productIssues',
          icon: <FlagOutlined />,
          path: '/app/admin/product-issues',
        },
        {
          id: 'admin-catalog-price-changes',
          labelKey: 'catalogPriceChanges',
          icon: <PriceChangeOutlined />,
          path: '/app/admin/catalog-price-changes',
        },
        {
          id: 'admin-pricing-freshness',
          labelKey: 'pricingFreshness',
          icon: <PriceCheckOutlined />,
          path: '/app/admin/pricing-freshness',
        },
        {
          id: 'admin-data-ingestion',
          labelKey: 'dataIngestion',
          icon: <CloudDownloadOutlined />,
          path: '/app/admin/data-ingestion',
        },
        {
          id: 'admin-jobs',
          labelKey: 'jobs',
          icon: <WorkHistoryOutlined />,
          path: '/app/admin/jobs',
        },
      ],
    },
    {
      id: 'platform-settings',
      labelKey: 'platformSettings',
      icon: <SettingsOutlined />,
      items: [
        {
          id: 'admin-billing',
          labelKey: 'billing',
          icon: <RequestQuoteOutlined />,
          path: '/app/admin/billing',
        },
        {
          id: 'admin-users',
          labelKey: 'users',
          icon: <SupervisedUserCircleOutlined />,
          path: '/app/admin/users',
        },
        {
          id: 'admin-plans',
          labelKey: 'plans',
          icon: <SellOutlined />,
          path: '/app/admin/plans',
        },
        {
          id: 'admin-notifications',
          labelKey: 'announcements',
          icon: <CampaignOutlined />,
          path: '/app/admin/notifications',
        },
        {
          id: 'admin-audit-logs',
          labelKey: 'auditLog',
          icon: <HistoryOutlined />,
          path: '/app/admin/audit-logs',
        },
        {
          id: 'admin-system',
          labelKey: 'system',
          icon: <SettingsOutlined />,
          path: '/app/admin/system',
        },
      ],
    },
    developerGroup,
  ],
};

const storeTree: NavTree = {
  roots: rootItems,
  groups: [
    {
      id: 'store-integrations',
      labelKey: 'integrations',
      icon: <HubOutlined />,
      items: [
        {
          id: 'store-integrations-link',
          labelKey: 'integrations',
          icon: <HubOutlined />,
          path: '/app/integrations',
          matchPaths: ['/app/integrations', '/app/integrations/:id'],
        },
      ],
    },
    {
      id: 'store-pricing',
      labelKey: 'pricing',
      icon: <LocalOfferOutlined />,
      items: [
        {
          id: 'map-pricing-data',
          labelKey: 'mapPricingData',
          icon: <LocalOfferOutlined />,
          path: '/app/map-pricing-data',
        },
        {
          id: 'shopify-pricing-audit',
          labelKey: 'shopifyPricingAudit',
          icon: <PriceCheckOutlined />,
          path: '/app/shopify-pricing-audit',
        },
        {
          id: 'price-history',
          labelKey: 'priceHistory',
          icon: <TuneOutlined />,
          path: '/app/price-history',
        },
        {
          id: 'change-recap',
          labelKey: 'changeRecap',
          icon: <PriceCheckOutlined />,
          path: '/app/change-recap',
          orgFlag: 'change_recap',
        },
        {
          id: 'pricing-preferences',
          labelKey: 'pricingPreferences',
          icon: <SettingsOutlined />,
          path: '/app/pricing-preferences',
        },
      ],
    },
    {
      id: 'store-operations',
      labelKey: 'operations',
      icon: <StorefrontOutlined />,
      items: [
        {
          id: 'store-settings',
          labelKey: 'store',
          icon: <StorefrontOutlined />,
          path: '/app/store-settings',
        },
        {
          id: 'store-group',
          labelKey: 'group',
          icon: <SupervisedUserCircleOutlined />,
          path: '/app/group',
          requiresGroupManager: true,
        },
        {
          id: 'store-team',
          labelKey: 'team',
          icon: <GroupOutlined />,
          path: '/app/team-management',
        },
        {
          id: 'sku-overrides',
          labelKey: 'skuOverrides',
          icon: <FindReplaceOutlined />,
          path: '/app/sku-overrides',
        },
        {
          id: 'store-bulk-ai',
          labelKey: 'bulkAi',
          icon: <AutoFixHighOutlined />,
          path: '/app/bulk-ai',
          orgFlag: 'bulk_ai_operations',
          planFeature: 'aiContentTools',
        },
        {
          id: 'store-bundles',
          labelKey: 'storeBundles',
          icon: <WidgetsOutlined />,
          path: '/app/store-bundles',
        },
        {
          id: 'sales-report',
          labelKey: 'salesReport',
          icon: <ShowChartOutlined />,
          path: '/app/sales-report',
        },
        {
          id: 'rebate-report',
          labelKey: 'rebateReport',
          icon: <SavingsOutlined />,
          path: '/app/rebate-report',
          planFeature: 'rebateTracking',
        },
        {
          id: 'store-serials',
          labelKey: 'serials',
          icon: <QrCode2Outlined />,
          path: '/app/serials',
          orgFlag: 'serial_capture',
          planFeature: 'rebateTracking',
        },
        {
          id: 'store-product-qa',
          labelKey: 'productQa',
          icon: <FactCheckOutlined />,
          path: '/app/store/staged-products',
          requiresProductQa: true,
        },
        {
          id: 'store-billing',
          labelKey: 'billing',
          icon: <ReceiptLongOutlined />,
          path: '/app/billing',
        },
      ],
    },
    developerGroup,
  ],
};

const vendorTree: NavTree = {
  roots: rootItems,
  groups: [
    {
      id: 'vendor-pricing',
      labelKey: 'pricing',
      icon: <TuneOutlined />,
      items: [
        {
          id: 'vendor-map-management',
          labelKey: 'mapManagement',
          icon: <TuneOutlined />,
          path: '/app/vendor-map-management',
        },
        {
          id: 'vendor-rebate-management',
          labelKey: 'rebateManagement',
          icon: <SavingsOutlined />,
          path: '/app/vendor-rebate-management',
          uiSwitch: 'ui_vendor_rebate_management',
        },
        {
          id: 'vendor-dealer-pricing',
          labelKey: 'dealerPricing',
          icon: <LocalOfferOutlined />,
          path: '/app/vendor-dealer-pricing',
          orgFlag: 'vendor_private_pricing',
        },
      ],
    },
    {
      id: 'vendor-brands',
      labelKey: 'brands',
      icon: <CategoryOutlined />,
      items: [
        {
          id: 'vendor-carry-requests',
          labelKey: 'brandsCarried',
          icon: <CategoryOutlined />,
          path: '/app/vendor-carry-requests',
          orgFlag: 'distributor_carry',
        },
      ],
    },
    {
      id: 'vendor-team',
      labelKey: 'team',
      icon: <GroupOutlined />,
      items: [
        {
          id: 'vendor-team-link',
          labelKey: 'team',
          icon: <GroupOutlined />,
          path: '/app/team-management',
        },
      ],
    },
    developerGroup,
  ],
};

export const navTrees: Record<ShellRole, NavTree> = {
  admin: adminTree,
  store: storeTree,
  vendor: vendorTree,
};

/** Reachable from the profile menu rather than the nav, but still palette-searchable. */
export const profileItem: NavLeaf = {
  id: 'profile',
  labelKey: 'profile',
  icon: <AccountCircleOutlined />,
  path: '/app/profile',
};

/**
 * `/app/admin/brands-vendors` is a prefix of three sibling routes, so a plain
 * `startsWith` would light the Directory row up on every one of them. Brand
 * detail pages *are* part of Directory and should keep it lit.
 */
export const DIRECTORY_PATH = '/app/admin/brands-vendors';
export const DIRECTORY_SIBLING_PATHS = [
  '/app/admin/brands-vendors/logos',
  '/app/admin/brands-vendors/organizations',
  '/app/admin/brands-vendors/approvals',
  '/app/admin/brands-vendors/dealer-lists',
  '/app/admin/brands-vendors/dealer-authorizations',
  '/app/admin/brands-vendors/price-clocks',
  '/app/admin/brands-vendors/locator-sources',
];
